import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

// Interfaces para los DTOs de Exchange Rate
export interface ExchangeRateConfigDTO {
  isEnabled: boolean;
  baseUrl: string;
  apiKey: string;
  updateIntervalHours: number;
  lastConfigUpdate?: string;
  lastRatesUpdate?: string;
}

export interface ExchangeRateConfigRequestDTO {
  isEnabled: boolean;
  baseUrl: string;
  apiKey: string;
  updateIntervalHours: number;
}

export interface CurrencyResponseDTO {
  id: number;
  code: string;
  name: string;
  symbol: string;
  isActive: boolean;
  isBaseCurrency: boolean;
  decimalPlaces: number;
  displayOrder: number;
  legacyEnumValue?: number;
  latestExchangeRate?: number;
  lastRateUpdate?: string;
}

export interface ExchangeRateResponseDTO {
  id: number;
  fromCurrencyId: number;
  fromCurrency: CurrencyResponseDTO;
  toCurrencyId: number;
  toCurrency: CurrencyResponseDTO;
  rate: number;
  effectiveDate: string;
  source: string;
  isActive: boolean;
  comments?: string;
  retrievedAt: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ExchangeRateHistoryResponse {
  data: ExchangeRateResponseDTO[];
  pagination: {
    page: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
  };
  filters: {
    fromCurrency?: string;
    toCurrency?: string;
  };
  timestamp: string;
  isSuccess: boolean;
}

export interface ExchangeRateLatestResponse {
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  timestamp: string;
  isSuccess: boolean;
}

export interface ExchangeRateConvertResponse {
  originalAmount: number;
  convertedAmount: number;
  fromCurrency: string;
  toCurrency: string;
  exchangeRate: number;
  timestamp: string;
  isSuccess: boolean;
}

export interface ExchangeRateStatusResponse {
  isEnabled: boolean;
  timestamp: string;
  isSuccess: boolean;
}

export interface ExchangeRateConfigResponse {
  data: ExchangeRateConfigDTO;
  timestamp: string;
  isSuccess: boolean;
}

export interface ApiResponse<T> {
  data?: T;
  message?: string;
  isSuccess: boolean;
  timestamp?: string;
  errors?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ExchangeRateService {
  private readonly apiUrl = `${environment.apiUrl}/ExchangeRate`;

  constructor(private http: HttpClient) { }

  /**
   * Obtiene la configuración actual del servicio de Exchange Rate
   */
  getConfiguration(): Observable<ExchangeRateConfigResponse> {
    return this.http.get<ExchangeRateConfigResponse>(`${this.apiUrl}/configuration`);
  }

  /**
   * Actualiza la configuración del servicio de Exchange Rate
   */
  updateConfiguration(config: ExchangeRateConfigRequestDTO): Observable<ApiResponse<any>> {
    return this.http.put<ApiResponse<any>>(`${this.apiUrl}/configuration`, config);
  }

  /**
   * Verifica si el servicio está habilitado
   */
  getServiceStatus(): Observable<ExchangeRateStatusResponse> {
    return this.http.get<ExchangeRateStatusResponse>(`${this.apiUrl}/status`);
  }

  /**
   * Obtiene la tasa de cambio más reciente entre dos monedas
   */
  getLatestRate(fromCurrency: string, toCurrency: string = 'COP'): Observable<ExchangeRateLatestResponse> {
    const params = new HttpParams()
      .set('fromCurrency', fromCurrency)
      .set('toCurrency', toCurrency);

    return this.http.get<ExchangeRateLatestResponse>(`${this.apiUrl}/latest`, { params });
  }

  /**
   * Convierte un monto de una moneda a otra
   */
  convertAmount(amount: number, fromCurrency: string, toCurrency: string = 'COP'): Observable<ExchangeRateConvertResponse> {
    const params = new HttpParams()
      .set('amount', amount.toString())
      .set('fromCurrency', fromCurrency)
      .set('toCurrency', toCurrency);

    return this.http.get<ExchangeRateConvertResponse>(`${this.apiUrl}/convert`, { params });
  }

  /**
   * Obtiene tasa histórica para una fecha específica
   */
  getHistoricalRate(fromCurrency: string, toCurrency: string, date: string): Observable<ExchangeRateLatestResponse> {
    const params = new HttpParams()
      .set('fromCurrency', fromCurrency)
      .set('toCurrency', toCurrency)
      .set('date', date);

    return this.http.get<ExchangeRateLatestResponse>(`${this.apiUrl}/historical`, { params });
  }

  /**
   * Obtiene todas las tasas activas para una moneda
   */
  getCurrencyRates(currencyCode: string): Observable<ApiResponse<ExchangeRateResponseDTO[]>> {
    return this.http.get<ApiResponse<ExchangeRateResponseDTO[]>>(`${this.apiUrl}/currency/${currencyCode}`);
  }

  /**
   * Actualiza manualmente las tasas de cambio
   */
  updateRates(): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/update`, {});
  }

  /**
   * Obtiene el histórico de tasas de cambio con filtros y paginación
   */
  getExchangeRateHistory(
    fromCurrency?: string,
    toCurrency?: string,
    page: number = 1,
    pageSize: number = 20,
    dateFrom?: string,
    dateTo?: string
  ): Observable<ExchangeRateHistoryResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (fromCurrency) {
      params = params.set('fromCurrency', fromCurrency);
    }
    if (toCurrency) {
      params = params.set('toCurrency', toCurrency);
    }
    if (dateFrom) {
      params = params.set('dateFrom', dateFrom);
    }
    if (dateTo) {
      params = params.set('dateTo', dateTo);
    }

    return this.http.get<ExchangeRateHistoryResponse>(`${this.apiUrl}/history`, { params });
  }
}